<template>
  <div class="platform-box" ref="myElementRef">
    <el-alert
      class="tip-bar"
      type="info"
      :closable="false"
      :title="t('platformAdmin.tenantsTip')"
    />
    <div ref="searchBox">
      <hc-search :data="searchData" @serch="getSearch">
        <template #btns>
          <el-button type="primary" icon="Plus" @click="openEdit()">{{ t('platformAdmin.addTenant') }}</el-button>
        </template>
      </hc-search>
    </div>

    <hc-table :loading="state.loading"
      :tableData="state.tableData"
      :column="state.tableHeader"
      :offsetHeight="state.offsetHeight"
      :maxPage="maxPage"
      @pageChange="pageChange"
      @limitChange="limitChange"
    >
      <template #tenant_type="{ row }">{{ tenantTypeLabel(row.tenant_type) }}</template>
      <template #status="{ row }">
        <el-tag :type="statusTagType(row)">{{ statusLabel(row) }}</el-tag>
      </template>
      <template #subscription_years="{ row }">
        <span v-if="row.tenant_type == 1 && row.tenant_code !== 'platform'">
          {{ row.subscription_years ? row.subscription_years + '年' : '—' }}
        </span>
        <span v-else>—</span>
      </template>
      <template #expire_at="{ row }">
        <span v-if="isSharedSaas(row)">
          {{ formatDate(row.expire_at) }}
          <el-tag v-if="isNotOpened(row)" type="warning" size="small" class="exp-tag">未开通</el-tag>
          <el-tag v-else-if="isExpired(row)" type="danger" size="small" class="exp-tag">已到期</el-tag>
        </span>
        <span v-else>—</span>
      </template>
      <template #admin_username="{ row }">
        <span v-if="row.tenant_code === 'platform'">—</span>
        <span v-else>{{ row.admin_username || '未设置' }}</span>
      </template>
      <template #admin_password="{ row }">
        <span v-if="row.tenant_code === 'platform'">—</span>
        <span v-else class="pwd-text">{{ row.admin_password || '—' }}</span>
      </template>
      <template #api_base_url="{ row }">{{ row.api_base_url || '（共享默认）' }}</template>
      <template #operation="{ row }">
        <el-button size="small" link type="primary" @click="openEdit(row)">编辑</el-button>
        <el-button
          v-if="row.tenant_code !== 'platform'"
          size="small"
          link
          :type="row.status == 1 ? 'warning' : 'success'"
          @click="toggleStatus(row)"
        >
          {{ row.status == 1 ? '停用' : '启用' }}
        </el-button>
      </template>
    </hc-table>

    <el-dialog v-model="editVisible" :title="form.id ? t('platformAdmin.editTenant') : t('platformAdmin.addTenant')" width="640px">
      <el-form ref="formRef" :model="form" :rules="rules" label-width="120px">
        <el-divider content-position="left">租户信息</el-divider>
        <el-form-item label="租户码" prop="tenant_code">
          <el-input v-model="form.tenant_code" :disabled="!!form.id" placeholder="小写英文，如 test、jinhui" />
        </el-form-item>
        <el-form-item label="租户名称" prop="tenant_name">
          <el-input v-model="form.tenant_name" placeholder="显示名称" />
        </el-form-item>
        <el-form-item label="部署类型" prop="tenant_type">
          <el-select v-model="form.tenant_type" style="width: 100%">
            <el-option label="共享租户（SaaS）" :value="1" />
            <el-option label="独立部署" :value="2" />
          </el-select>
        </el-form-item>
        <el-form-item v-if="form.tenant_type === 2" label="API 地址" prop="api_base_url">
          <el-input v-model="form.api_base_url" placeholder="https://客户域名/nginx转发地址/" />
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-select v-model="form.status" style="width: 100%" :disabled="form.tenant_code === 'platform'">
            <el-option label="启用" :value="1" />
            <el-option label="停用" :value="0" />
          </el-select>
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="form.remark" type="textarea" rows="2" />
        </el-form-item>

        <template v-if="form.tenant_code !== 'platform'">
          <el-divider content-position="left">租户后台管理员</el-divider>
          <p class="field-hint">管理端登录：租户码 + 下方账号密码（超管列表可查看明文密码）</p>
          <el-form-item label="登录账号" prop="admin_username">
            <el-input
              v-model="form.admin_username"
              placeholder="手机号、邮箱或字母+数字（至少8位）"
              autocomplete="off"
            />
            <p class="field-hint">{{ LOGIN_ACCOUNT_HINT }}</p>
          </el-form-item>
          <el-form-item label="登录密码" prop="admin_password">
            <el-input
              v-model="form.admin_password"
              placeholder="新建必填；编辑可修改"
              autocomplete="new-password"
            />
          </el-form-item>
          <el-form-item label="姓名">
            <el-input v-model="form.admin_real_name" placeholder="选填" />
          </el-form-item>
        </template>
      </el-form>
      <template #footer>
        <el-button @click="editVisible = false">取消</el-button>
        <el-button type="primary" @click="submitForm">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { reactive, ref, onMounted, nextTick, computed } from 'vue'
import hcSearch from '@/components/hc-search/index.vue'
import hcTable from '@/components/hc-table/index.vue'
import { tenantListApi, tenantSaveApi, tenantUpdateStatusApi } from '@/api/platform/tenants'
import { ElMessage, ElMessageBox } from 'element-plus'
import { t } from '@/composables/usePageI18n.js'
import {
  isValidLoginAccount,
  normalizeLoginAccount,
  LOGIN_ACCOUNT_HINT,
  LOGIN_ACCOUNT_INVALID_MSG
} from '@/utils/loginAccount'

const myElementRef = ref()
const searchBox = ref()
const formRef = ref()
const maxPage = ref(0)
const page = ref(1)
const limit = ref(20)
const editVisible = ref(false)

const searchData = reactive([
  { label: '关键词', type: 'input', value: '', field: 'keywords', placeholder: '租户码/名称/管理员账号' }
])

const form = reactive({
  id: null,
  tenant_code: '',
  tenant_name: '',
  tenant_type: 1,
  api_base_url: '',
  status: 1,
  remark: '',
  admin_username: '',
  admin_password: '',
  admin_real_name: ''
})

const validateAdminUsername = (rule, value, callback) => {
  if (form.tenant_code === 'platform') {
    callback()
    return
  }
  const v = value == null ? '' : String(value).trim()
  if (!form.id && !v) {
    callback(new Error('请设置租户后台登录账号'))
    return
  }
  if (v && !isValidLoginAccount(v)) {
    callback(new Error(LOGIN_ACCOUNT_INVALID_MSG))
    return
  }
  callback()
}

const validateAdminPassword = (rule, value, callback) => {
  if (form.tenant_code === 'platform') {
    callback()
    return
  }
  if (!form.id && (!value || !String(value).trim())) {
    callback(new Error('请设置租户后台登录密码'))
    return
  }
  callback()
}

const rules = computed(() => ({
  tenant_code: [{ required: true, message: '请输入租户码', trigger: 'blur' }],
  tenant_name: [{ required: true, message: '请输入租户名称', trigger: 'blur' }],
  tenant_type: [{ required: true, message: '请选择部署类型', trigger: 'change' }],
  admin_username: [{ validator: validateAdminUsername, trigger: 'blur' }],
  admin_password: [{ validator: validateAdminPassword, trigger: 'blur' }]
}))

const state = reactive({
	loading: false,
  offsetHeight: 0,
  tableData: [],
  tableHeader: [],
  column: [
    { type: 'index', label: '序号', width: 70, show: true },
    { prop: 'tenant_code', label: '租户码', width: 100, show: true },
    { prop: 'tenant_name', label: '租户名称', width: 120, show: true },
    { prop: 'admin_username', label: '后台账号', width: 100, show: true },
    { prop: 'admin_password', label: '后台密码', width: 100, show: true },
    { prop: 'subscription_years', label: '年限', width: 70, show: true },
    { prop: 'expire_at', label: '到期时间', width: 175, show: true },
    { prop: 'tenant_type', label: '类型', width: 100, show: true },
    { prop: 'status', label: '状态', width: 90, show: true },
    { prop: 'operation', label: '操作', width: 130, show: true }
  ]
})

const tenantTypeLabel = (t) => {
  if (t === 2 || t === '2') return '独立部署'
  return '共享 SaaS'
}

const formatDate = (val) => {
  if (!val) return '—'
  const s = String(val)
  return s.length >= 16 ? s.substring(0, 16) : s
}

const isSharedSaas = (row) => row.tenant_type == 1 && row.tenant_code !== 'platform'

const isNotOpened = (row) => isSharedSaas(row) && !row.opened_at

const isExpired = (row) => {
  if (!isSharedSaas(row) || !row.expire_at) return false
  return new Date(row.expire_at) < new Date()
}

const statusLabel = (row) => {
  if (row.status != 1) return '停用'
  if (isNotOpened(row)) return '未开通'
  if (isExpired(row)) return '已到期'
  return '启用'
}

const statusTagType = (row) => {
  if (row.status != 1) return 'info'
  if (isNotOpened(row)) return 'warning'
  if (isExpired(row)) return 'danger'
  return 'success'
}

const tableHeaderFn = () => {
  state.tableHeader = state.column.filter((v) => v.show)
}

const offHeighths = () => {
  if (myElementRef.value) {
    state.offsetHeight = myElementRef.value.offsetHeight - 120 - (searchBox.value?.offsetHeight || 0)
  }
}

const resetForm = () => {
  form.id = null
  form.tenant_code = ''
  form.tenant_name = ''
  form.tenant_type = 1
  form.api_base_url = ''
  form.status = 1
  form.remark = ''
  form.admin_username = ''
  form.admin_password = ''
  form.admin_real_name = ''
}

const openEdit = (row) => {
  resetForm()
  if (row) {
    form.id = row.id
    form.tenant_code = row.tenant_code
    form.tenant_name = row.tenant_name
    form.tenant_type = Number(row.tenant_type) || 1
    form.api_base_url = row.api_base_url || ''
    form.status = Number(row.status)
    form.remark = row.remark || ''
    form.admin_username = row.admin_username || ''
    form.admin_password = row.admin_password || ''
    form.admin_real_name = row.admin_real_name || ''
  }
  editVisible.value = true
}

const getSearch = (params) => {
  const payload = {
    page: page.value,
    limit: limit.value,
    keywords: params?.keywords ?? searchData[0].value ?? ''
  }
  state.loading = true
  tenantListApi(payload).then((res) => {
    if (res.code === 200) {
      state.tableData = res.data || []
      maxPage.value = res.maxPage || 0
    } else {
      ElMessage.error(res.msg || '加载失败')
    }
  }).finally(() => { state.loading = false })
}

const pageChange = (p) => {
  page.value = p
  getSearch()
}

const limitChange = (l) => {
  limit.value = l
  page.value = 1
  getSearch()
}

const submitForm = () => {
  formRef.value.validate((valid) => {
    if (!valid) return
    const payload = { ...form }
    if (form.id && !payload.admin_password) {
      delete payload.admin_password
    }
    if (payload.admin_username && String(payload.admin_username).trim()) {
      const normalized = normalizeLoginAccount(payload.admin_username)
      if (normalized) {
        payload.admin_username = normalized
      }
    }
    tenantSaveApi(payload).then((res) => {
      if (res.code === 200) {
        ElMessage.success(res.msg || '保存成功')
        editVisible.value = false
        getSearch()
      } else {
        ElMessage.error(res.msg || '保存失败')
      }
    })
  })
}

const toggleStatus = (row) => {
  const next = row.status == 1 ? 0 : 1
  const action = next === 1 ? '启用' : '停用'
  ElMessageBox.confirm(`确认${action}租户「${row.tenant_name}」？`, '提示', { type: 'warning' })
    .then(() => {
      tenantUpdateStatusApi({ id: row.id, status: next }).then((res) => {
        if (res.code === 200) {
          ElMessage.success(res.msg || '操作成功')
          getSearch()
        } else {
          ElMessage.error(res.msg || '操作失败')
        }
      })
    })
    .catch(() => {})
}

onMounted(() => {
  tableHeaderFn()
  nextTick(() => {
    offHeighths()
    getSearch()
  })
})
</script>

<style lang="less" scoped>
.platform-box {
  flex: 1;
  overflow: auto;
  background: #fff;
  border-radius: 6px;
  padding: 10px;
  margin-right: 10px;
}
.tip-bar {
  margin-bottom: 12px;
}
.field-hint {
  margin: 0 0 12px 120px;
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
}
.inline-hint {
  margin: 4px 0 0 0;
}
.date-preview {
  font-size: 13px;
  color: #606266;
}
.pwd-text {
  font-family: monospace;
  color: #c45656;
}
.exp-tag {
  margin-left: 6px;
}
</style>
